import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, ROLE_META } from "@/context/AuthContext";
import { Lock, Mail, ArrowRight, ShieldCheck } from "lucide-react";

const QUICK = [
  { role: "ceo", email: "ceo@polarisae.com" },
  { role: "admin", email: "enquiry@polarisae.com", label: "BDM", sub: "Business Dev" },
  { role: "pmo", email: "sapm@polarisae.com" },
  { role: "commercial", email: "commercial@polarisae.com" },
  { role: "design", email: "design@polarisae.com" },
  { role: "material", email: "material@polarisae.com" },
  { role: "procurement", email: "procurement@polarisae.com" },
  { role: "production", email: "production@polarisae.com" },
  { role: "projects", email: "projects@polarisae.com" },
];

export default function Login() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("Polaris@2026");
  const [busy, setBusy] = useState(false);

  const submit = async (e, em) => {
    e?.preventDefault();
    setBusy(true);
    const ok = await login(em || email, password);
    setBusy(false);
    if (ok) navigate("/");
  };

  return (
    <div className="min-h-screen w-full flex polaris-bg">
      {/* Left brand panel — brand CSS gradient/pattern (no stock imagery) */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden border-r border-border">
        {/* base steel gradient */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(155deg, #0a0a0d 0%, #15171d 48%, #0d0e12 78%, #0a0a0d 100%)" }}
        />
        {/* gold aurora glow, top-left */}
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(120% 90% at 18% 12%, rgba(201,162,90,0.16), rgba(201,162,90,0.02) 42%, transparent 62%)" }}
        />
        {/* cool steel highlight, bottom-right */}
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(90% 80% at 92% 96%, rgba(120,140,170,0.10), transparent 55%)" }}
        />
        {/* fine grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.9) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(120% 100% at 30% 20%, black, transparent 80%)",
            WebkitMaskImage: "radial-gradient(120% 100% at 30% 20%, black, transparent 80%)",
          }}
        />
        {/* top vignette for text legibility */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,13,0.35), transparent 30%, transparent 60%, rgba(10,10,13,0.55))" }} />

        <div className="relative z-10 p-12">
          <div className="flex items-center gap-3">
            <img src="/polaris-emblem.png" alt="Polaris" className="w-14 h-14 object-contain" />
            <div>
              <div className="font-heading text-2xl tracking-tight">Polaris</div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-gold/80">International Industries</div>
            </div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative z-10 p-12"
        >
          <div className="w-14 h-px bg-gradient-to-r from-gold to-transparent mb-7" />
          <h1 className="font-heading text-5xl xl:text-6xl leading-[1.05] tracking-tight max-w-xl">
            Do the <span className="gold-text">right task</span> next<span className="text-gold">.</span>
            <br />
            <span className="text-muted-foreground/90">Not the loudest one.</span>
          </h1>
          <p className="text-muted-foreground mt-6 max-w-md text-base leading-relaxed">
            Priority Command ranks every stage across all live projects by contractual deadline risk,
            money at stake and client importance.
          </p>
        </motion.div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/polaris-emblem.png" alt="Polaris" className="w-12 h-12 object-contain" />
            <div>
              <div className="font-heading text-xl">Polaris</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-gold/80">Priority Command</div>
            </div>
          </div>

          <h2 className="font-heading text-3xl mb-1">Sign in</h2>
          <p className="text-sm text-muted-foreground mb-7">Access your command center.</p>

          <form onSubmit={submit} className="space-y-4">
            <Field icon={Mail} label="Work email">
              <input
                data-testid="login-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@polarisae.com" required
                className="w-full bg-transparent outline-none text-sm placeholder:text-muted-foreground/60"
              />
            </Field>
            <Field icon={Lock} label="Password">
              <input
                data-testid="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required
                className="w-full bg-transparent outline-none text-sm placeholder:text-muted-foreground/60"
              />
            </Field>

            {error && (
              <div data-testid="login-error" className="text-sm text-critical bg-critical/10 border border-critical/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit" disabled={busy}
              data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 bg-gold text-primary-foreground font-semibold py-3 rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"} <ArrowRight size={17} />
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-border">
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-3">
              <ShieldCheck size={13} className="text-gold" /> Demo quick access (password pre-filled)
            </div>
            <div className="grid grid-cols-3 gap-2">
              {QUICK.map((q) => (
                <button
                  key={q.role}
                  data-testid={`quick-${q.role}`}
                  onClick={(e) => submit(e, q.email)}
                  className="text-left px-3 py-2.5 rounded-md border border-border hover:border-gold/40 hover:bg-muted/50 transition-all"
                >
                  <div className="font-medium text-xs leading-tight">{q.label || ROLE_META[q.role].label}</div>
                  <div className="text-[10px] text-muted-foreground/70 mt-0.5">{q.sub || ROLE_META[q.role].short}</div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground mb-1.5 block">{label}</span>
      <div className="flex items-center gap-2.5 px-3.5 py-3 rounded-md bg-input border border-border focus-within:border-gold/50 transition-colors">
        <Icon size={16} className="text-muted-foreground" />
        {children}
      </div>
    </label>
  );
}
